import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { useSendMessage } from '@/hooks/useWhatsAppMessages';
import { useWhatsAppTemplates } from '@/hooks/useWhatsAppTemplates';
import { useToast } from '@/hooks/use-toast';

interface WhatsAppMessageComposerProps {
  leadId?: string;
  phoneNumber?: string;
}

export const WhatsAppMessageComposer: React.FC<WhatsAppMessageComposerProps> = ({
  leadId,
  phoneNumber: initialPhoneNumber,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || '');
  const [content, setContent] = useState('');
  const [messageType, setMessageType] = useState<string>('MANUAL');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const { data: templates } = useWhatsAppTemplates({ isActive: true });
  const sendMessage = useSendMessage();
  const { toast } = useToast();

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates?.find((t) => t.id === templateId);
    if (template) {
      setContent(template.content);
    }
  };

  const handleSend = async () => {
    if (!phoneNumber || !content) {
      toast({
        title: 'Validation Error',
        description: 'Phone number and message content are required',
        variant: 'destructive',
      });
      return;
    }

    if (content.length > 4096) {
      toast({
        title: 'Validation Error',
        description: 'Message content cannot exceed 4096 characters',
        variant: 'destructive',
      });
      return;
    }

    try {
      await sendMessage.mutateAsync({
        phoneNumber,
        content,
        type: messageType as any,
        leadId,
      });

      toast({
        title: 'Success',
        description: 'Message sent successfully',
      });

      // Reset form
      setContent('');
      if (!initialPhoneNumber) {
        setPhoneNumber('');
      }
      setSelectedTemplateId('');
      setMessageType('MANUAL');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      });
    }
  };

  const insertPlaceholder = (placeholder: string) => {
    setContent((prev) => prev + `{{${placeholder}}}`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Send className="h-5 w-5" />
          <span>Send WhatsApp Message</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Phone Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <Input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            disabled={!!initialPhoneNumber}
          />
          <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +91)</p>
        </div>

        {/* Message Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message Type
          </label>
          <Select value={messageType} onValueChange={setMessageType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MANUAL">Manual</SelectItem>
              <SelectItem value="AUTO">Auto</SelectItem>
              <SelectItem value="FOLLOW_UP">Follow-up</SelectItem>
              <SelectItem value="REMINDER">Reminder</SelectItem>
              <SelectItem value="PROPOSAL">Proposal</SelectItem>
              <SelectItem value="NEGOTIATION">Negotiation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Template Selector */}
        {templates && templates.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Use Template (Optional)
            </label>
            <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.category})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Message Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message Content *
          </label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter your message..."
            rows={6}
            className="resize-none"
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-gray-500">
              {content.length} / 4096 characters
            </p>
            {content.length > 4096 && (
              <p className="text-xs text-red-500">Character limit exceeded</p>
            )}
          </div>
        </div>

        {/* Placeholder Buttons */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Insert Placeholders
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => insertPlaceholder('leadName')}
            >
              Lead Name
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => insertPlaceholder('companyName')}
            >
              Company Name
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => insertPlaceholder('dealValue')}
            >
              Deal Value
            </Button>
          </div>
        </div>

        {/* AI Generate Button */}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => {
            // This will be implemented when AI integration is ready
            toast({
              title: 'AI Generation',
              description: 'AI message generation coming soon!',
            });
          }}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Generate with AI
        </Button>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={!phoneNumber || !content || content.length > 4096 || sendMessage.isPending}
          className="w-full"
        >
          {sendMessage.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
